import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { Image } from "@/components/ui/image";
import { X, ZoomIn, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
// ✅ Import de todas las imágenes de la galería
import c1 from "@/assets/images/galeria/532112de3_c1.jpeg";
import c2 from "@/assets/images/galeria/2147df093_c2.jpeg";
import c3 from "@/assets/images/galeria/7e708283f_c3.jpeg";
import c4 from "@/assets/images/galeria/87074625e_c4.jpeg";
import c5 from "@/assets/images/galeria/1c3e9dc9e_c5.jpeg";
import c6 from "@/assets/images/galeria/6a0642758_c6.jpeg";
import c7 from "@/assets/images/galeria/99ecb9627_c7.jpeg";
import c8 from "@/assets/images/galeria/e4a49b075_c8.jpeg";
import b1 from "@/assets/images/galeria/9caa95b06_B1.jpeg";
import b2 from "@/assets/images/galeria/7cce30a18_B2.jpeg";
import b3 from "@/assets/images/galeria/8bfc5ca2e_B3.jpeg";
import b4 from "@/assets/images/galeria/184ae3a68_B4.jpeg";
import p1 from "@/assets/images/galeria/fe2ffd122_P1.jpeg";
import p2 from "@/assets/images/galeria/bd04da915_P2.jpeg";
import p3 from "@/assets/images/galeria/ffc284a99_P3.jpeg";
import bb1 from "@/assets/images/galeria/fdd71e700_BB1.jpeg";
import bb2 from "@/assets/images/galeria/87fdad61a_BB2.jpeg";
import bb3 from "@/assets/images/galeria/48865ac35_BB3.jpeg";
import j1 from "@/assets/images/galeria/9361792b3_J1.jpg";
import j2 from "@/assets/images/galeria/f9695f1e1_J2.jpg";
import j3 from "@/assets/images/galeria/bbbfaf770_J3.jpg";
import j4 from "@/assets/images/galeria/fe933075c_J4.jpg";
import je1 from "@/assets/images/galeria/4d5834553_JE1.jpeg";
import je2 from "@/assets/images/galeria/3d170b154_JE2.jpeg";
import je3 from "@/assets/images/galeria/df6ab1267_JE3.jpeg";
import je4 from "@/assets/images/galeria/fda122f2d_JE4.jpeg";

// ✅ Array de todas las imágenes
const POOL = [
  c1, c2, c3, c4, c5, c6, c7, c8,
  b1, b2, b3, b4,
  p1, p2, p3,
  bb1, bb2, bb3,
  j1, j2, j3, j4,
  je1, je2, je3, je4,
];

const CATEGORIAS = [
  {
    id: "casa-principal",
    nombre: "Casa Principal",
    imagenes: [POOL[0], POOL[1], POOL[2], POOL[3], POOL[4], POOL[5], POOL[6], POOL[7]],
    titulo: "CASA PRINCIPAL",
    descripcion: ["COCINA | COMEDOR | SALA | 4 HABITACIONES CON BAÑOS COMPLETOS"],
    recamaras: [
      { nombre: "Recámara Principal", caracteristicas: ["CAMA KING SIZE", "BAÑO", "T.V. POR CABLE"] },
      { nombre: "Segunda Recámara", caracteristicas: ["2 CAMAS MATRIMONIALES", "1 CAMA INDIVIDUAL", "BAÑO", "T.V. POR CABLE"] },
      { nombre: "Tercera Recámara", caracteristicas: ["2 CAMAS MATRIMONIALES", "BAÑO", "T.V. POR CABLE"] },
      { nombre: "Cuarta Recámara", caracteristicas: ["3 CAMAS INDIVIDUALES", "BAÑO"] },
    ],
  },
  {
    id: "bungalow",
    nombre: "Bungalow",
    imagenes: [POOL[8], POOL[9], POOL[10], POOL[11]],
    titulo: "BUNGALOW",
    descripcion: ["COCINETA | COMEDOR | SALA | 2 RECAMARAS"],
    recamaras: [
      { nombre: "Recámara 1", caracteristicas: ["2 CAMAS MATRIMONIALES", "1 CAMA INDIVIDUAL", "1 BAÑO COMPLETO"] },
      { nombre: "Recámara 2", caracteristicas: ["2 CAMAS MATRIMONIALES", "1 BAÑO COMPLETO"] },
    ],
  },
  {
    id: "palapa",
    nombre: "Palapa",
    imagenes: [POOL[12], POOL[13], POOL[14]],
    titulo: "PALAPA",
    descripcion: ["ESTUFA | ASADOR", "SALA | REFRIGERADOR | BARRA", "ALBERCA | JACUZZI | CAMASTROS"],
  },
  {
    id: "bodega-bar",
    nombre: "Bodega Bar",
    imagenes: [POOL[15], POOL[16], POOL[17]],
    titulo: "BODEGA BAR",
    descripcion: ["MESA DE BILLAR | T.V. POR CABLE", "MESA DE CARTAS | BARRA"],
  },
  {
    id: "jardin",
    nombre: "Jardín",
    imagenes: [POOL[18], POOL[19], POOL[20], POOL[21]],
    titulo: "JARDÍN",
    descripcion: ["EXTENSO JARDÍN 3,000.00 MTS2"],
  },
  {
    id: "jardin-eventos",
    nombre: "Jardín de Eventos",
    imagenes: [POOL[22], POOL[23], POOL[24], POOL[25]],
    titulo: "JARDÍN DE EVENTOS",
    descripcion: [
      "Alauda es el lugar perfecto para hacer realidad tu evento especial. Ubicado en una zona privilegiada de la ciudad, este impresionante jardín de eventos cuenta con un ambiente elegante y sofisticado que dejará a tus invitados impresionados. Con capacidad para hasta 600 personas, es el espacio ideal para bodas, quinceañeras, eventos corporativos y cualquier otra celebración única que desees. ¡Haz que tu evento sea inolvidable en Alauda!",
    ],
    esParrafo: true,
  },
];

function DescripcionCategoria({ categoria }) {
  return (
    <div className="space-y-6">
      <h3 className="font-serif-display text-2xl md:text-3xl font-light text-foreground tracking-wide">
        {categoria.titulo}
      </h3>
      <div className="space-y-2">
        {categoria.descripcion.map((linea, i) =>
          categoria.esParrafo ? (
            <p key={i} className="text-foreground/70 font-light leading-relaxed text-sm md:text-base">
              {linea}
            </p>
          ) : (
            <p
              key={i}
              className="text-xs md:text-sm tracking-[0.15em] uppercase font-light text-foreground/70"
            >
              {linea}
            </p>
          )
        )}
      </div>

      {categoria.recamaras && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-border">
          {categoria.recamaras.map((rec) => (
            <div key={rec.nombre} className="bg-secondary/30 border border-border rounded-sm p-5">
              <h4 className="text-sm font-light text-foreground mb-3 tracking-wide">
                {rec.nombre}
              </h4>
              <ul className="space-y-1.5">
                {rec.caracteristicas.map((c) => (
                  <li
                    key={c}
                    className="text-xs tracking-[0.1em] uppercase font-light text-muted-foreground flex items-center gap-2"
                  >
                    <span className="w-1 h-1 rounded-full bg-accent flex-shrink-0" />
                    {c}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Galeria() {
  const { ref, isVisible } = useScrollReveal();
  const [activa, setActiva] = useState(CATEGORIAS[0].id);
  const [lightbox, setLightbox] = useState(null);

  const categoriaActual = CATEGORIAS.find((c) => c.id === activa);
  const imagenesActuales = categoriaActual.imagenes;

  const closeLightbox = useCallback(() => setLightbox(null), []);
  const nextImage = useCallback(
    () => setLightbox((prev) => (prev + 1) % imagenesActuales.length),
    [imagenesActuales.length]
  );
  const prevImage = useCallback(
    () => setLightbox((prev) => (prev - 1 + imagenesActuales.length) % imagenesActuales.length),
    [imagenesActuales.length]
  );

  return (
    <section id="galeria" ref={ref} className="py-28 md:py-40 px-6 lg:px-10 bg-background">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <motion.span
            initial={{ opacity: 0 }}
            animate={isVisible ? { opacity: 1 } : {}}
            transition={{ duration: 1 }}
            className="text-accent text-sm tracking-[0.3em] uppercase font-light mb-6 block"
          >
            Galería
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1.2, delay: 0.1 }}
            className="font-serif-display text-4xl md:text-5xl lg:text-6xl font-light text-foreground text-balance"
          >
            Un espacio para admirar
          </motion.h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8 lg:gap-12">
          {/* Botones de categorías */}
          <div className="lg:sticky lg:top-24 lg:self-start">
            <div className="flex lg:flex-col gap-2 overflow-x-auto no-scrollbar pb-2 lg:pb-0">
              {CATEGORIAS.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => {
                    setActiva(cat.id);
                    setLightbox(null);
                  }}
                  className={cn(
                    "px-5 py-3 text-left whitespace-nowrap lg:whitespace-normal rounded-sm border transition-colors duration-300",
                    activa === cat.id
                      ? "bg-foreground text-background border-foreground"
                      : "bg-card text-foreground/70 border-border hover:border-accent/40 hover:text-foreground"
                  )}
                >
                  <span className="text-sm font-light tracking-wide">{cat.nombre}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Contenido: imágenes + descripción */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activa}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.5 }}
            >
              {/* Imágenes arriba */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mb-10">
                {imagenesActuales.map((src, i) => (
                  <motion.div
                    key={src}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, delay: i * 0.08 }}
                    className="group relative overflow-hidden cursor-pointer aspect-[4/3]"
                    onClick={() => setLightbox(i)}
                  >
                    <Image
                      src={src}
                      alt={`${categoriaActual.nombre} ${i + 1}`}
                      className="w-full h-full transition-transform duration-700 group-hover:scale-110"
                      fittingType="fill"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                      <ZoomIn
                        size={22}
                        className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      />
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Descripción abajo */}
              <DescripcionCategoria categoria={categoriaActual} />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-6"
            onClick={closeLightbox}
          >
            <button
              className="absolute top-6 right-6 text-white/70 hover:text-white p-2"
              onClick={closeLightbox}
            >
              <X size={28} />
            </button>
            <motion.div
              key={lightbox}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="max-w-5xl max-h-[85vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={imagenesActuales[lightbox]}
                alt={`${categoriaActual.nombre} ${lightbox + 1}`}
                className="max-w-full max-h-[85vh] object-contain"
              />
            </motion.div>
            <button
              className="absolute left-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2"
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
            >
              <ChevronLeft size={32} />
            </button>
            <button
              className="absolute right-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2"
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
            >
              <ChevronRight size={32} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}